/**
 * Social Media Agent Hooks
 * Jotai atoms and hooks for the comment-moderation rules.
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { socialAgentService } from '@/services/social-agent.service';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { invalidateQueries } from '@/lib/query-client';
import type { SocialRuleStatus } from '@/modules/social-agent/types';

const socialRulesQueryAtom = atomWithQuery((get) => ({
	// The brand belongs in the KEY as well as the request. A brand switch does not clear queries,
	// so a key without it serves the previous brand's rules to the next one.
	queryKey: ['social-agent', 'rules', buScopeKey(get)],
	queryFn: () => socialAgentService.listRules(buScopeParams(get)),
	staleTime: 30 * 1000
}));

const setRuleStatusMutationAtom = atomWithMutation(() => ({
	mutationKey: ['social-agent', 'rules', 'status'],
	mutationFn: ({ ruleId, status }: { ruleId: string; status: SocialRuleStatus }) =>
		socialAgentService.setRuleStatus(ruleId, status),
	onSuccess: () => {
		invalidateQueries(['social-agent']);
	}
}));

const deleteRuleMutationAtom = atomWithMutation(() => ({
	mutationKey: ['social-agent', 'rules', 'delete'],
	mutationFn: (ruleId: string) => socialAgentService.deleteRule(ruleId),
	onSuccess: () => {
		invalidateQueries(['social-agent']);
	}
}));

export function useSocialRules() {
	const [query] = useAtom(socialRulesQueryAtom);
	return query;
}

export function useSetSocialRuleStatus() {
	const [mutation] = useAtom(setRuleStatusMutationAtom);
	return mutation;
}

export function useDeleteSocialRule() {
	const [mutation] = useAtom(deleteRuleMutationAtom);
	return mutation;
}
